import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { Building2, LogOut, Plus, Settings, Share2, User, Users } from "lucide-react";
import type { Organization, SpaceRef, Team } from "@etus-mindmap/shared/api";
import { useMe, useOrgLogo, useSpaces } from "../api/hooks";
import { spaceHeader, useActiveSpace } from "../api/space-context";
import { ThemeToggle } from "./theme";
import { CreateSpaceDrawer } from "./CreateSpaceDrawer";

// Sidebar com switcher de espaço. Times que pertencem a uma org aparecem
// aninhados sob ela; times sem org ficam num grupo "Times". Gerenciar membros /
// configurações da org são telas de navegação (rotas), não drawers.

const PERSONAL: SpaceRef = { type: "personal", id: null };

function OrgLogoIcon({ org }: { org: Organization }) {
  const logo = useOrgLogo(org.id, org.hasLogo);
  if (org.hasLogo && logo.data) {
    return <img src={logo.data} alt="" className="h-4 w-4 shrink-0 rounded object-contain" />;
  }
  return <Building2 className="h-4 w-4 shrink-0" aria-hidden />;
}

export function Sidebar() {
  const { user, logout } = useAuth0();
  const me = useMe();
  const spaces = useSpaces();
  const { activeSpace, setActiveSpace } = useActiveSpace();
  const navigate = useNavigate();
  const [createKind, setCreateKind] = useState<"org" | "team" | null>(null);

  // Trocar de espaço sempre leva à listagem de mapas — senão, estando numa tela
  // de config/membros, o clique só mudaria o espaço e não voltaria.
  const selectSpace = (s: SpaceRef) => {
    setActiveSpace(s);
    navigate({ to: "/" });
  };

  const activeKey = spaceHeader(activeSpace);
  const orgs = spaces.data?.orgs ?? [];
  const teams = spaces.data?.teams ?? [];
  const teamsByOrg = (orgId: number) => teams.filter((t) => t.orgId === orgId);
  const looseTeams = teams.filter((t) => t.orgId === null);

  const rowCls = (active: boolean, indent = false) =>
    [
      "flex flex-1 items-center gap-2 rounded-md py-1.5 text-sm font-medium transition-colors",
      indent ? "pl-8 pr-3" : "px-3",
      active
        ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-light"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100",
    ].join(" ");
  const gearCls =
    "flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200";

  const teamRow = (t: Team, indent: boolean) => (
    <div key={`team-${t.id}`} className="flex items-center gap-0.5">
      <button type="button" onClick={() => selectSpace({ type: "team", id: t.id })} className={rowCls(activeKey === `team:${t.id}`, indent)}>
        <Users className="h-4 w-4 shrink-0" aria-hidden />
        <span className="truncate">{t.name}</span>
      </button>
      <Link to="/teams/$teamId/members" params={{ teamId: String(t.id) }} title="Membros" aria-label="Membros do time" className={gearCls}>
        <Users className="h-3.5 w-3.5" aria-hidden />
      </Link>
    </div>
  );

  return (
    <aside className="hidden h-screen w-60 shrink-0 flex-col border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 sm:flex">
      <div className="border-b border-slate-200 px-4 py-4 dark:border-slate-800">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src="/etus-logo.png" alt="" className="h-9 w-9 rounded-lg shadow-sm" />
          <span className="text-xl font-semibold text-slate-900 dark:text-slate-100">Mindmap</span>
        </Link>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
        <button type="button" onClick={() => selectSpace(PERSONAL)} className={rowCls(activeKey === "personal")}>
          <User className="h-4 w-4 shrink-0" aria-hidden />
          Pessoal
        </button>
        <button
          type="button"
          onClick={() => selectSpace({ type: "shared", id: null })}
          className={rowCls(activeKey === "shared")}
        >
          <Share2 className="h-4 w-4 shrink-0" aria-hidden />
          Compartilhados comigo
        </button>

        {orgs.length > 0 && (
          <p className="px-3 pt-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Organizações
          </p>
        )}
        {orgs.map((o) => (
          <div key={`org-${o.id}`}>
            <div className="flex items-center gap-0.5">
              <button type="button" onClick={() => selectSpace({ type: "org", id: o.id })} className={rowCls(activeKey === `org:${o.id}`)}>
                <OrgLogoIcon org={o} />
                <span className="truncate">{o.name}</span>
              </button>
              <Link to="/orgs/$orgId" params={{ orgId: String(o.id) }} title="Configurações" aria-label="Configurações da org" className={gearCls}>
                <Settings className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </div>
            {teamsByOrg(o.id).map((t) => teamRow(t, true))}
          </div>
        ))}

        {looseTeams.length > 0 && (
          <p className="px-3 pt-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Times
          </p>
        )}
        {looseTeams.map((t) => teamRow(t, false))}

        <div className="space-y-0.5 pt-3">
          <button
            type="button"
            onClick={() => setCreateKind("org")}
            className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> Nova organização
          </button>
          <button
            type="button"
            onClick={() => setCreateKind("team")}
            className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> Novo time
          </button>
        </div>
      </nav>

      <div className="space-y-3 border-t border-slate-200 px-4 py-3 dark:border-slate-800">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-xs text-slate-500 dark:text-slate-400" title={user?.email}>
            {user?.email}
            {me.data?.role === "admin" && " · admin"}
          </span>
          <button
            type="button"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            title="Sair"
            aria-label="Sair"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <LogOut className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <ThemeToggle />
      </div>

      {createKind && <CreateSpaceDrawer kind={createKind} onClose={() => setCreateKind(null)} />}
    </aside>
  );
}
